import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Toaster } from 'sonner';

// Auth Components
import LoginForm from './pages/auth/LoginForm';
import RegisterForm from './pages/auth/RegisterForm';
import ForgotPasswordForm from './pages/auth/ForgotPasswordForm';
import ProtectedRoute from './components/common/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';

// Page Components
import DashboardPage from './pages/dashboard/DashboardPage';
import GuestSignContractPage from './pages/contracts/GuestSignContractPage';
import PublicPartnershipFormPage from './pages/partnerships/PublicPartnershipFormPage';
import { BrandPortalPage } from './pages/portal';
import NotFoundPage from './components/ui/NotFoundPage';

// Auth Store
import { useAuthStore } from './store/authStore';

// Styles
import './styles/globals.css';

// Create QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {
      retry: 1,
    },
  },
});

// Auth Guard Component
const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuthStore();

  React.useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token && !user) {
      // Optionally verify token with backend
    }
  }, [user]);

  return <>{children}</>;
};

// Root redirect logic
const RootRedirect: React.FC = () => {
  const { isAuthenticated } = useAuthStore();

  return (
    <Navigate
      to={isAuthenticated ? "/dashboard" : "/login"}
      replace
    />
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <Router>
          <AuthGuard>
            <div className="App">
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<LoginForm />} />
                <Route path="/register" element={<RegisterForm />} />
                <Route path="/forgot-password" element={<ForgotPasswordForm />} />

                {/* Public Guest Signing Route */}
                <Route path="/p/contracts/:id/sign" element={<GuestSignContractPage />} />

                {/* Public Partnership Form Route */}
                <Route path="/p/partnerships/:teamId" element={<PublicPartnershipFormPage />} />

                {/* Public Brand Portal Route */}
                <Route path="/p/review/:token" element={<BrandPortalPage />} />

                {/* Protected Dashboard Routes */}
                <Route path="/dashboard/*" element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                } />

                {/* Root Redirect and 404 */}
                <Route path="/" element={<RootRedirect />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </div>
          </AuthGuard>
        </Router>
      </ErrorBoundary>
      <Toaster position="top-right" richColors />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

export default App;